/**
 * Source unique des informations entreprise (NAP, horaires, zones, tarifs).
 * Utilisée par le footer, l'accueil, les pages locales et les données structurées JSON-LD.
 * Toute modification ici se répercute sur l'ensemble du site.
 */

export const SITE_URL = 'https://sos-it-services.fr';

/** Lien direct vers le formulaire d'avis Google Business Profile. */
export const GOOGLE_REVIEW_URL = 'https://g.page/r/CVVnzbhiAoZyEAE/review';

export const BUSINESS = {
  name: 'SOS IT Services',
  url: SITE_URL,
  email: 'contact@sos-it-services.fr',
  phoneDisplay: '06 26 74 82 99',
  phoneTel: '+33626748299',
  whatsappUrl: 'https://wa.me/33626748299',
  // Pas d'adresse postale ni de géolocalisation publiées volontairement :
  // activité à domicile sur zone d'intervention, locaux = domicile du dirigeant.
  address: {
    postalCode: '74580',
    city: 'Viry',
    country: 'France',
    countryCode: 'FR',
  },
  /** Formulation unique du délai de réponse (réparation téléphone et dépannage). */
  responseLabel: 'Réponse en moins de 2h, intervention souvent le jour même',
  /** Délai spécifique aux devis de création de site web. */
  websiteQuoteResponseLabel: 'Réponse sous 48h pour les devis de création de site web',
  warrantyLabel: 'garantie 90 jours',
  sameAs: [
    'https://www.instagram.com/sos.it.services/',
    'https://www.tiktok.com/@sos.it.services',
    'https://www.youtube.com/@sos.it.services',
    'https://www.facebook.com/sos.it.services',
  ],
} as const;

/** Fondateur : section « Qui suis-je » et entité Person du JSON-LD. */
export const FOUNDER = {
  name: 'Lucas Olive',
  jobTitle: 'Fondateur',
  /** Photo portrait (fichier à fournir dans public/). */
  photo: '/images/brand/lucas-fondateur.webp',
  linkedin: 'https://www.linkedin.com/in/lucasolive/',
} as const;

/** Liste historique de zones (pages réparation et site web). La zone de la homepage, le footer et l'areaServed du JSON-LD utilisent COVERAGE_TOWNS. */
export const SERVICE_AREAS: string[] = [
  'Viry',
  'Saint-Julien-en-Genevois',
  'Genève',
  'Annemasse',
  'Archamps',
  'Neydens',
  'Collonges-sous-Salève',
  'Reignier-Ésery',
  'Étrembières',
  'Pays de Gex',
  'La Roche-sur-Foron',
  'Bonneville',
];

export interface PriceItem {
  id: string;
  label: string;
  /** Prix « à partir de » en euros ; null = sur devis. */
  priceFrom: number | null;
  /** Complément affiché après le prix (ex. abonnement mensuel). */
  priceSuffix?: string;
  category: 'telephone' | 'informatique' | 'web';
}

/** Tarifs « à partir de ». */
export const PRICES: PriceItem[] = [
  { id: 'batterie', label: 'Remplacement batterie', priceFrom: 60, category: 'telephone' },
  { id: 'ecran', label: 'Remplacement écran', priceFrom: 80, category: 'telephone' },
  { id: 'connecteur-nettoyage', label: 'Connecteur de charge — nettoyage', priceFrom: 30, category: 'telephone' },
  { id: 'connecteur', label: 'Connecteur de charge — remplacement', priceFrom: 90, category: 'telephone' },
  { id: 'vitre-arriere', label: 'Vitre arrière', priceFrom: 100, category: 'telephone' },
  { id: 'camera', label: 'Caméra', priceFrom: 150, category: 'telephone' },
  { id: 'lentille-camera', label: 'Lentille caméra', priceFrom: 40, category: 'telephone' },
  { id: 'depannage-pc', label: 'Dépannage PC / Mac', priceFrom: null, category: 'informatique' },
  { id: 'recuperation-donnees', label: 'Récupération de données', priceFrom: null, category: 'informatique' },
  { id: 'site-vitrine', label: 'Site vitrine', priceFrom: 350, priceSuffix: 'puis 80 €/mois', category: 'web' },
];

export const formatPrice = (item: PriceItem): string =>
  item.priceFrom === null
    ? 'Sur devis'
    : `à partir de ${item.priceFrom} €${item.priceSuffix ? ` ${item.priceSuffix}` : ''}`;

/** « 60 € » (espace insécable, évite le retour à la ligne avant le symbole). */
const NBSP = String.fromCharCode(0xa0);
export const formatEuro = (amount: number): string => `${amount}${NBSP}€`;

export const PHONE_PRICES = PRICES.filter((p) => p.category === 'telephone');

export const BRANDS = ['iPhone', 'Samsung', 'Xiaomi', 'iPad', 'PC', 'Mac'];

/** Pages locales « réparation téléphone » (maillage footer / pages services). */
export const PHONE_REPAIR_LOCAL_PAGES = [
  { city: 'Viry', label: 'Réparation téléphone Viry', href: '/reparation-telephone-viry' },
  { city: 'Saint-Julien-en-Genevois', label: 'Réparation téléphone Saint-Julien', href: '/reparation-telephone-saint-julien-en-genevois' },
  { city: 'Genève', label: 'Réparation iPhone Genève', href: '/reparation-iphone-geneve' },
  { city: 'Annemasse', label: 'Réparation téléphone Annemasse', href: '/reparation-telephone-annemasse' },
];
